import { z } from "zod"
import {
  CapabilityExecutionStatus,
  CapabilityIdentifier,
  CapabilityKind,
} from "../capabilities/dtos"
import {
  ConversationCustomerResponseDataSchema,
  ConversationOpportunityResponseDataSchema,
  ConversationResponseDataSchema,
  ConversationResponseResponseDataSchema,
  ConversationTicketIntegrationKindEnum,
  MessageResponseDataSchema,
} from "../conversations/response_data"
import { CompanyClientResponseDataSchema } from "../customers/response_data"
import { GoalResponseDataSchema } from "../goals/response_data"
import {
  IssueUrgencyEnum,
  IssueStatusEnum,
  SupportConversationTicketResponseDataSchema,
} from "../support/response_data"
import { WorkflowEventEnum } from "./response_data"

type WorkflowEventPayloads = Record<keyof typeof WorkflowEventEnum, z.ZodType>

const ConversationEventSchema = z
  .union([z.record(z.string(), z.any()), z.number(), z.boolean(), z.string()])
  .nullish()

const CompanyClientWithExternalIdResponseDataSchema = CompanyClientResponseDataSchema.transform(
  (client) => ({
    id: client.id,
    externalId: client.externalId,
    name: client.name,
  }),
)

const ConversationCustomerWithClientResponseDataSchema =
  ConversationCustomerResponseDataSchema.extend({
    client: CompanyClientWithExternalIdResponseDataSchema.nullish(),
  })

export const DigestedConversationResponseDataSchema = z.object({
  data: ConversationResponseDataSchema.omit({
    conversationTickets: true,
    conversationQuestions: true,
    customer: true,
  }).extend(
    z.object({
      customer: ConversationCustomerWithClientResponseDataSchema,
      messages: z.array(MessageResponseDataSchema).optional(),
      externalId: z.string().nullish(),
      url: z.string(),
    }).shape,
  ),
})

const CampaignConversationPayloadSchema = DigestedConversationResponseDataSchema.shape.data.omit({
  customer: true,
})

export const DigestedMessageResponseDataSchema = z.object({
  data: MessageResponseDataSchema.extend({
    customer: ConversationCustomerWithClientResponseDataSchema,
    conversation: CampaignConversationPayloadSchema,
  }),
})

export const DigestedConversationTicketResponseDataSchema = z.object({
  data: SupportConversationTicketResponseDataSchema.extend(
    z.object({
      summary: z.string(),
      customer: ConversationCustomerWithClientResponseDataSchema,
      conversation: CampaignConversationPayloadSchema,
      conversationQuestions: z.array(
        z.object({
          id: z.bigint().transform((id) => id.toString()),
          summary: z.string(),
          automaticallyResolvedAt: z
            .date()
            .transform((date) => date.toISOString())
            .nullish(),
        }),
      ),
    }).shape,
  ),
})

export const DigestedIssueCreatedResponseDataSchema = z.object({
  data: z.object({
    id: z.bigint().transform((id) => id.toString()),
    subject: z.string(),
    summary: z.string(),
    urgency: z.enum(IssueUrgencyEnum),
    status: z.enum(IssueStatusEnum),
    isHandoffRequest: z.boolean(),
    occurrence: z.object({
      id: z.bigint().transform((id) => id.toString()),
      sequence: z.number(),
      summary: z.string(),
      createdAt: z.date().transform((value) => value.toISOString()),
    }),
    customer: ConversationCustomerWithClientResponseDataSchema,
    conversation: CampaignConversationPayloadSchema,
  }),
})

export const DigestedConversationOpportunityResponseDataSchema = z.object({
  data: ConversationOpportunityResponseDataSchema.omit({
    conversationId: true,
    escalatedAt: true,
  }).extend(
    z.object({
      conversation: CampaignConversationPayloadSchema,
      customer: ConversationCustomerWithClientResponseDataSchema,
      evaluationData: z.record(z.string(), z.any()).optional(),
    }).shape,
  ),
})

export const DigestedGoalResponseDataSchema = z.object({
  data: GoalResponseDataSchema.omit({ secondTurnBias: true }).extend(
    z.object({
      event: ConversationEventSchema,
      customer: ConversationCustomerWithClientResponseDataSchema,
      conversation: CampaignConversationPayloadSchema,
      responses: z.array(
        ConversationResponseResponseDataSchema.omit({
          goalName: true,
          goalDisplayName: true,
          goalType: true,
          customer: true,
          customerId: true,
          conversationId: true,
        }),
      ),
    }).shape,
  ),
})

export const DigestedCapabilityEvaluatedResponseDataSchema = z.object({
  data: z.object({
    capabilityId: z.string(),
    capabilityName: z.string(),
    identifier: z.enum(CapabilityIdentifier),
    kind: z.enum(CapabilityKind),
    evaluationData: z.record(z.string(), z.any()),
    conversation: CampaignConversationPayloadSchema,
    customer: ConversationCustomerWithClientResponseDataSchema,
  }),
})

export const DigestedCapabilityExecutedResponseDataSchema = z.object({
  data: DigestedCapabilityEvaluatedResponseDataSchema.shape.data.extend({
    executionStatus: z.enum(CapabilityExecutionStatus),
    executionResult: z.record(z.string(), z.any()).nullish(),
    executionReason: z.string().nullish(),
  }),
})

export const WorkflowEventPayloads = {
  [WorkflowEventEnum.ConversationStarted]: DigestedConversationResponseDataSchema,
  [WorkflowEventEnum.ConversationAchieved]: DigestedConversationResponseDataSchema,
  [WorkflowEventEnum.ConversationClosed]: DigestedConversationResponseDataSchema,
  [WorkflowEventEnum.ConversationFailed]: DigestedConversationResponseDataSchema,
  [WorkflowEventEnum.ConversationMessageCreated]: DigestedMessageResponseDataSchema,
  [WorkflowEventEnum.ConversationTicketCreated]: DigestedConversationTicketResponseDataSchema,
  [WorkflowEventEnum.IssueCreated]: DigestedIssueCreatedResponseDataSchema,
  [WorkflowEventEnum.ConversationGoalAchieved]: DigestedGoalResponseDataSchema,
  [WorkflowEventEnum.UpsellOpportunity]: DigestedConversationOpportunityResponseDataSchema,
  [WorkflowEventEnum.CapabilityEvaluated]: DigestedCapabilityEvaluatedResponseDataSchema,
  [WorkflowEventEnum.CapabilityExecuted]: DigestedCapabilityExecutedResponseDataSchema,
} as const

const customerExample = {
  id: "1",
  name: "John Doe",
  email: "john.doe@apple.com",
  phone: "+1234567890",
  metadata: {
    age: 30,
    location: "USA",
    orderId: "1234567890",
  },
  client: {
    id: "1",
    externalId: "client_ref_1",
    name: "Acme Logistics",
  },
}

const conversationExample = {
  data: {
    id: "1",
    externalId: "conv_abc",
    url: "https://app.permut.com/conversations/list/1?page=1&field=createdAt&order=desc&conversationId=1&tab=conversations",
    status: "Pending" as const,
    type: "Live" as const,
    createdAt: new Date("2025-01-01T00:00:00Z").toISOString(),
    updatedAt: new Date("2025-01-01T00:00:00Z").toISOString(),
    closedAt: new Date("2025-01-01T00:00:00Z").toISOString(),
    summary: "This is a summary of the conversation",
    event: {
      market: "SURVEY",
      id: "12345",
    },
    hasMessages: false,
    aiDisabled: false,
    aiDisabledPermanently: false,
    aiDisabledUntil: null,
  },
}

const conversationWithCustomerExample = {
  data: {
    ...conversationExample.data,
    customer: customerExample,
  },
}

const conversationOpportunityExample = {
  data: {
    id: "1",
    summary: "This is a summary of the opportunity",
    qualification: "Potential" as const,
    createdAt: new Date("2025-01-01T00:00:00Z").toISOString(),
    customer: customerExample,
    conversation: conversationExample.data,
    evaluationData: {
      isBusinessOpportunity: true,
      isBusinessOpportunityReason: "Customer expressed interest in upgrading their plan.",
      someCustomRequiredField: "value",
    },
  },
}

const messageExample = {
  data: {
    id: "1",
    role: "User" as const,
    content: "Hello, how are you?",
    mediaUrl: null,
    format: "Text" as const,
    createdAt: new Date("2025-01-01T00:00:00Z").toISOString(),
    conversationId: "1",
    customer: customerExample,
    conversation: conversationExample.data,
    isGenerative: false,
  },
}

const conversationTicketExample = {
  data: {
    id: "1",
    conversationId: "1",
    summary: "This is a summary of the ticket",
    status: "Pending" as const,
    createdAt: new Date("2025-01-01T00:00:00Z").toISOString(),
    updatedAt: new Date("2025-01-01T00:00:00Z").toISOString(),
    integrations: [
      {
        externalId: "1",
        kind: ConversationTicketIntegrationKindEnum.Zendesk,
        createdAt: new Date("2025-01-01T00:00:00Z").toISOString(),
      },
    ],
    customer: customerExample,
    conversation: {
      ...conversationExample.data,
      messages: [
        {
          id: "1",
          role: "Assistant" as const,
          content: "Hello, how are you?",
          format: "Text" as const,
          mediaUrl: null,
          createdAt: new Date("2025-01-01T00:00:00Z").toISOString(),
          conversationId: "1",
        },
        {
          id: "2",
          role: "User" as const,
          content: "I'm good, thank you!",
          format: "Text" as const,
          mediaUrl: null,
          createdAt: new Date("2025-01-01T00:00:00Z").toISOString(),
          conversationId: "1",
        },
        {
          id: "3",
          role: "User" as const,
          content: "What is the status of my refund?",
          format: "Text" as const,
          mediaUrl: null,
          createdAt: new Date("2025-01-01T00:00:00Z").toISOString(),
          conversationId: "1",
        },
        {
          id: "4",
          role: "Assistant" as const,
          content: "Let me check that for you and I'll get back to you very shortly.",
          format: "Text" as const,
          mediaUrl: null,
          createdAt: new Date("2025-01-01T00:00:00Z").toISOString(),
          conversationId: "1",
        },
        {
          id: "5",
          role: "User" as const,
          content: null,
          format: "Image" as const,
          mediaUrl: "https://example.com/images/damaged-product.jpg",
          createdAt: new Date("2025-01-01T00:00:00Z").toISOString(),
          conversationId: "1",
        },
        {
          id: "6",
          role: "User" as const,
          content: "Here's a photo of the damaged item I received",
          format: "Text" as const,
          mediaUrl: null,
          createdAt: new Date("2025-01-01T00:00:00Z").toISOString(),
          conversationId: "1",
        },
        {
          id: "7",
          role: "Assistant" as const,
          content:
            "I can see the damage in the photo. I sincerely apologize for this inconvenience.",
          format: "Text" as const,
          mediaUrl: null,
          createdAt: new Date("2025-01-01T00:00:00Z").toISOString(),
          conversationId: "1",
        },
        {
          id: "8",
          role: "User" as const,
          content: null,
          format: "Audio" as const,
          mediaUrl: "https://example.com/audio/voice-message.mp3",
          transcription:
            "I've been waiting for two weeks now and I really need this resolved urgently because I need the product for an important event next week.",
          createdAt: new Date("2025-01-01T00:00:00Z").toISOString(),
          conversationId: "1",
        },
        {
          id: "9",
          role: "Assistant" as const,
          content:
            "I completely understand your urgency. I'm processing your full refund right now and escalating this to our logistics team.",
          format: "Text" as const,
          mediaUrl: null,
          createdAt: new Date("2025-01-01T00:00:00Z").toISOString(),
          conversationId: "1",
        },
        {
          id: "10",
          role: "User" as const,
          content: null,
          format: "Video" as const,
          mediaUrl: "https://example.com/videos/unboxing.mp4",
          createdAt: new Date("2025-01-01T00:00:00Z").toISOString(),
          conversationId: "1",
        },
        {
          id: "11",
          role: "User" as const,
          content: "This video shows the condition when I opened the package",
          format: "Text" as const,
          mediaUrl: null,
          createdAt: new Date("2025-01-01T00:00:00Z").toISOString(),
          conversationId: "1",
        },
        {
          id: "12",
          role: "Assistant" as const,
          content:
            "Thank you for the detailed documentation. Your refund has been approved and you should receive it within 3-5 business days. I've also added a 20% discount code for your next purchase as an apology.",
          format: "Text" as const,
          mediaUrl: null,
          createdAt: new Date("2025-01-01T00:00:00Z").toISOString(),
          conversationId: "1",
        },
      ],
    },
    conversationQuestions: [
      {
        id: "1",
        summary: "The customer is asking for a refund",
        automaticallyResolvedAt: new Date("2025-01-01T00:00:00Z").toISOString(),
      },
    ],
  },
}

const goalExample = {
  data: {
    id: "1",
    type: "Open" as const,
    name: "Customer satisfaction",
    assistantInstructions: "Measures customer satisfaction with our service and product experience",
    insightsInstructions: null,
    displayName: "Customer satisfaction",
    slug: "customer-satisfaction",
    frequency: "Monthly" as const,
    shots: 2,
    goalOptions: [
      {
        id: "opt_1",
        value: "Detractor",
      },
      {
        id: "opt_2",
        value: "Passive",
      },
      {
        id: "opt_3",
        value: "Promoter",
      },
    ],
    customer: customerExample,
    conversation: conversationExample.data,
    event: {
      market: "SURVEY",
      id: "12345",
    },
    responses: [
      {
        id: "1",
        createdAt: "2025-09-15T14:32:21Z",
        conversationId: "2",
        customer: customerExample,
        customerId: "1",
        goalId: "12345",
        response: "I'm very satisfied with the product, but the delivery was a bit slow.",
        subject: "Product Feedback",
        sentiment: 3.2,
        goalOptionId: "1",
        goalOptionValue: "Promoter",
      },
    ],
    createdAt: new Date("2025-01-01T00:00:00Z").toISOString(),
  },
}

const capabilityEvaluatedExample = {
  data: {
    capabilityId: "1",
    capabilityName: "Business opportunity",
    identifier: CapabilityIdentifier.BusinessOpportunity,
    kind: "Native" as CapabilityKind,
    evaluationData: {
      isBusinessOpportunity: true,
      isBusinessOpportunityReason: "Customer expressed interest in upgrading their plan.",
    },
    conversation: conversationExample.data,
    customer: customerExample,
  },
}

const capabilityExecutedExample = {
  data: {
    ...capabilityEvaluatedExample.data,
    executionStatus: CapabilityExecutionStatus.Success,
    executionResult: {},
  },
}

const issueCreatedExample = {
  data: {
    id: "1",
    subject: "Damaged delivery",
    summary: "The customer received a damaged item and needs support.",
    urgency: "High" as const,
    status: "Open" as const,
    isHandoffRequest: true,
    occurrence: {
      id: "1",
      sequence: 1,
      summary: "The customer shared photos of the damaged item.",
      createdAt: new Date("2025-01-01T00:00:00Z").toISOString(),
    },
    conversation: conversationExample.data,
    customer: customerExample,
  },
}

type WorkflowEventPayloadsExamples = Record<keyof typeof WorkflowEventEnum, unknown>

export const WorkflowEventPayloadsExamples = {
  [WorkflowEventEnum.ConversationStarted]: conversationWithCustomerExample satisfies z.infer<
    typeof DigestedConversationResponseDataSchema
  >,
  [WorkflowEventEnum.ConversationAchieved]: conversationWithCustomerExample satisfies z.infer<
    typeof DigestedConversationResponseDataSchema
  >,
  [WorkflowEventEnum.ConversationClosed]: conversationWithCustomerExample satisfies z.infer<
    typeof DigestedConversationResponseDataSchema
  >,
  [WorkflowEventEnum.ConversationFailed]: conversationWithCustomerExample satisfies z.infer<
    typeof DigestedConversationResponseDataSchema
  >,
  [WorkflowEventEnum.ConversationMessageCreated]: messageExample satisfies z.infer<
    typeof DigestedMessageResponseDataSchema
  >,
  [WorkflowEventEnum.ConversationTicketCreated]: conversationTicketExample satisfies z.infer<
    typeof DigestedConversationTicketResponseDataSchema
  >,
  [WorkflowEventEnum.IssueCreated]: issueCreatedExample satisfies z.infer<
    typeof DigestedIssueCreatedResponseDataSchema
  >,
  [WorkflowEventEnum.ConversationGoalAchieved]: goalExample satisfies z.infer<
    typeof DigestedGoalResponseDataSchema
  >,
  [WorkflowEventEnum.UpsellOpportunity]: conversationOpportunityExample satisfies z.infer<
    typeof DigestedConversationOpportunityResponseDataSchema
  >,
  [WorkflowEventEnum.CapabilityEvaluated]: capabilityEvaluatedExample satisfies z.infer<
    typeof DigestedCapabilityEvaluatedResponseDataSchema
  >,
  [WorkflowEventEnum.CapabilityExecuted]: capabilityExecutedExample satisfies z.infer<
    typeof DigestedCapabilityExecutedResponseDataSchema
  >,
} as const satisfies WorkflowEventPayloadsExamples
